import React, { useState, useEffect } from 'react';
import {
	Select,
	FormControl,
	Menu,
	MenuItem,
	InputAdornment,
	IconButton,
	InputLabel,
	Typography,
	Popover
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import makeStyles from '@mui/styles/makeStyles';
import { useWindowDimensions } from '../../../../../hook/customerHook';
import { AccessTime } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(1),
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between'
	},
	display: { display: 'none' },
	icons: {
		display: 'flex',
		padding: 0,
		margin: 0,
		alignItems: 'center'
	},
	labels: {
		display: 'flex',
		alignItems: 'center'
	},
	fields: {
		marginTop: 0,
		display: 'flex',
		alignItems: 'center'
	},
	popup: {
		padding: theme.spacing(1)
	},
	select: {
		minWidth: 150
	},
	datePicker: {
		marginRight: '10px',
		maxWidth: 150
	},
	form: {
		padding: theme.spacing(1)
	}
}));

function DateFields({ startDate, endDate, setStartDate, setEndDate }) {
	const classes = useStyles();
	const [selectValue, setSelectValue] = useState(
		localStorage.getItem('timeframeFilters') ? localStorage.getItem('timeframeFilters') : ''
	);

	const timeFilter = [
		{ label: 'All', value: '' },
		{ label: 'Last 7 days', value: 7 },
		{ label: 'Last 30 days', value: 30 },
		{ label: 'Last 60 days', value: 60 },
		{ label: 'Last 90 days', value: 90 },
		{ label: 'Specify range', value: 'range' }
	];

	const handleDropdownChange = (event) => {
		let time = event.target.value;
		if (time > 0) {
			setSelectValue(time);
			setStartDate(moment().subtract(time, 'days').toISOString());
			setEndDate(moment().toISOString());
		} else if (time === '') {
			setSelectValue('');
			setStartDate('');
			setEndDate(moment().toISOString());
		} else if (time === 'range') {
			setSelectValue(time);
		}
		localStorage.setItem('timeframeFilters', time);
	};

	const handleStartDateChange = (newValue) => {
		if (newValue) {
			setStartDate(newValue.toISOString());
			localStorage.setItem('startTime', newValue.toISOString());
		}
	};

	const handleEndDateChange = (newValue) => {
		if (newValue) {
			setEndDate(newValue.toISOString());
			localStorage.setItem('endTime', newValue.toISOString());
		}
	};

	return (
		<>
			<div className={classes.popup}>
				<FormControl component="fieldset" variant="outlined" className={classes.timeFrame}>
					<InputLabel>Timeframe</InputLabel>
					<Select
						onChange={handleDropdownChange}
						className={classes.select}
						value={
							localStorage.getItem('timeframeFilters')
								? localStorage.getItem('timeframeFilters')
								: selectValue
						}
						label="Timeframe"
					>
						{timeFilter.map((options, id) => (
							<MenuItem key={id} value={options.value}>
								{options.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</div>
			{selectValue === 'range' && (
				<LocalizationProvider dateAdapter={AdapterMoment}>
					<form className={classes.form}>
						<DatePicker
							className={classes.datePicker}
							label="From"
							value={startDate ? moment(startDate) : null}
							onChange={handleStartDateChange}
							slotProps={{
								textField: {
									InputProps: {
										endAdornment: (
											<InputAdornment
												position="end"
												style={{
													maxHeight: 'none',
													height: 'auto',
													marginTop: '-1px',
													marginRight: '-10px',
													marginBottom: '-1px'
												}}
											>
												<IconButton size="small">
													<ArrowDropDownIcon />
												</IconButton>
											</InputAdornment>
										)
									}
								}
							}}
						/>
						<DatePicker
							className={classes.datePicker}
							label="To"
							value={endDate ? moment(endDate) : null}
							minDate={startDate ? moment(startDate) : null}
							onChange={handleEndDateChange}
							slotProps={{
								textField: {
									InputProps: {
										endAdornment: (
											<InputAdornment
												position="end"
												style={{
													maxHeight: 'none',
													height: 'auto',
													marginTop: '-1px',
													marginRight: '-10px',
													marginBottom: '-1px'
												}}
											>
												<IconButton size="small">
													<ArrowDropDownIcon />
												</IconButton>
											</InputAdornment>
										)
									}
								}
							}}
						/>
					</form>
				</LocalizationProvider>
			)}
		</>
	);
}

export default function DateFilter({ startDate, endDate, setStartDate, setEndDate }) {
	const [anchorEl, setAnchorEl] = useState(null);
	const dimensions = useWindowDimensions();

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const open = Boolean(anchorEl);

	const id = open ? 'simple-popover' : undefined;

	useEffect(() => {
		if (
			localStorage.getItem('timeframeFilters') &&
			localStorage.getItem('timeframeFilters') == 'range'
		) {
			const start = localStorage.getItem('startTime');
			const end = localStorage.getItem('endTime');
			if (end) setEndDate(end);
			if (start) setStartDate(start);
		}
	}, []);

	return (
		<>
			{dimensions.width < 960 ? (
				<>
					<IconButton
						aria-label="add"
						color={startDate ? 'secondary' : 'inherit'}
						onClick={handleClick}
						aria-describedby={id}
						size="large"
					>
						<AccessTime />
					</IconButton>
					<Popover
						id={id}
						open={open}
						anchorEl={anchorEl}
						onClose={handleClose}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'center'
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'center'
						}}
					>
						<DateFields
							startDate={startDate}
							endDate={endDate}
							setStartDate={setStartDate}
							setEndDate={setEndDate}
						/>
					</Popover>
				</>
			) : (
				<DateFields
					startDate={startDate}
					endDate={endDate}
					setStartDate={setStartDate}
					setEndDate={setEndDate}
				/>
			)}
		</>
	);
}
